import React, { useState, useRef, useEffect, useCallback } from 'react';
import { mockContacts } from './emailTypes';
import { useAppStore } from '../../stores/appStore';

interface ComposeModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (draft: { to: string; cc: string; bcc: string; subject: string; body: string }) => void | Promise<void>;
  onSchedule?: () => void;
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
      <path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M6.25 4.85L7.05 4.05C8.1 3 9.8 3 10.85 4.05C11.9 5.1 11.9 6.8 10.85 7.85L10.05 8.65M8.75 10.15L7.95 10.95C6.9 12 5.2 12 4.15 10.95C3.1 9.9 3.1 8.2 4.15 7.15L4.95 6.35M5.95 9.05L9.05 5.95"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true" focusable="false">
      <path d="M5.25 4.25H11.5M5.25 7.5H11.5M5.25 10.75H11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M3.5 4.25H3.55M3.5 7.5H3.55M3.5 10.75H3.55" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function AIWriteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
      <path d="M7 2L8.2 5.8L12 7L8.2 8.2L7 12L5.8 8.2L2 7L5.8 5.8L7 2Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round" />
      <path d="M11 2V4M10 3H12" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );
}

export function ComposeModal({ open, onClose, onSend }: ComposeModalProps) {
  const composePrefill = useAppStore((s) => s.composePrefill);
  const setComposePrefill = useAppStore((s) => s.setComposePrefill);

  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [focusedField, setFocusedField] = useState<'to' | 'body' | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const filteredContacts = mockContacts.filter(
    (c) =>
      c.name.toLowerCase().includes(contactSearch.toLowerCase()) ||
      c.email.toLowerCase().includes(contactSearch.toLowerCase())
  );

  // Pre-fill from appStore when opening (AI draft or SignalList action)
  useEffect(() => {
    if (!open) return;
    if (composePrefill) {
      setTo(composePrefill.to);
      setSubject(composePrefill.subject);
      setBody(composePrefill.body);
      setComposePrefill(null);
    }
  }, [open, composePrefill, setComposePrefill]);

  const handleSend = useCallback(async () => {
    if (isSending) return;
    if (!to.trim() || !body.trim()) return;

    setSendError(null);
    setIsSending(true);
    try {
      await onSend({ to, cc, bcc, subject, body });
      onClose();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  }, [isSending, to, cc, bcc, subject, body, onSend, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (body || subject || to) {
          setShowDiscardConfirm(true);
        } else {
          onClose();
        }
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (to && subject && body) {
          void handleSend();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, body, subject, to, onClose, handleSend]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTo('');
      setCc('');
      setBcc('');
      setSubject('');
      setBody('');
      setShowCc(false);
      setShowBcc(false);
      setShowContactDropdown(false);
      setShowDiscardConfirm(false);
      setContactSearch('');
      setSendError(null);
      setIsSending(false);
    }
  }, [open]);

  const handleContactSelect = (contact: typeof mockContacts[0]) => {
    setTo(contact.email);
    setShowContactDropdown(false);
    setContactSearch('');
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: 'var(--bg-overlay)' }}
        onClick={() => {
          if (body || subject || to) setShowDiscardConfirm(true);
          else onClose();
        }}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed left-1/2 top-1/2 z-50 flex flex-col rounded-xl"
        style={{
          width: '640px',
          maxHeight: '85vh',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'var(--bg-surface)',
          border: '1px solid var(--border-strong)',
          boxShadow: 'var(--shadow-lg)',
          outline: 'none',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Compose new email"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <span
            className="text-sm font-semibold"
            style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans, system-ui, sans-serif' }}
          >
            New Message
          </span>
          <button
            className="w-7 h-7 flex items-center justify-center rounded text-sm font-mono transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            aria-label="Close compose modal"
            onClick={() => {
              if (body || subject || to) setShowDiscardConfirm(true);
              else onClose();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--text-tertiary)';
            }}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Fields */}
        <div className="flex flex-col shrink-0" style={{ borderBottom: '1px solid var(--border)' }}>
          {/* To */}
          <div className="flex items-center gap-2 px-5 py-2">
            <span className="text-sm font-mono shrink-0" style={{ color: 'var(--text-tertiary)', width: '32px' }}>To</span>
            <div className="relative flex-1">
              <input
                type="text"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setShowContactDropdown(true);
                  setContactSearch(e.target.value);
                }}
                onFocus={() => {
                  setFocusedField('to');
                  setShowContactDropdown(true);
                  setContactSearch(to);
                }}
                onBlur={() => {
                  setTimeout(() => setShowContactDropdown(false), 150);
                }}
                placeholder="recipient@email.com"
                className="w-full bg-transparent text-sm outline-none"
                style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans, system-ui, sans-serif' }}
              />
              {/* Contact autocomplete dropdown */}
              {showContactDropdown && focusedField === 'to' && filteredContacts.length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 rounded-lg z-50 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--bg-elevated)',
                    border: '1px solid var(--border-strong)',
                    boxShadow: 'var(--shadow-lg)',
                    marginTop: '4px',
                  }}
                >
                  {filteredContacts.map((c) => (
                    <button
                      key={c.email}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left transition-colors"
                      style={{}}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      onMouseDown={() => handleContactSelect(c)}
                    >
                      <div
                        className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full text-xs font-mono"
                        style={{ backgroundColor: 'var(--accent)', color: 'var(--text-inverse)' }}
                      >
                        {c.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{c.name}</p>
                        <p className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>{c.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cc/Bcc toggle */}
          <div className="flex items-center gap-4 px-5 pb-1">
            <button
              className="text-xs transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onClick={() => setShowCc(!showCc)}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            >
              {showCc ? 'Hide Cc' : 'Show Cc'}
            </button>
            <button
              className="text-xs transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onClick={() => setShowBcc(!showBcc)}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            >
              {showBcc ? 'Hide Bcc' : 'Show Bcc'}
            </button>
          </div>

          {/* Cc */}
          {showCc && (
            <div className="flex items-center gap-2 px-5 py-1">
              <span className="text-sm font-mono shrink-0" style={{ color: 'var(--text-tertiary)', width: '32px' }}>Cc</span>
              <input
                type="text"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none"
                style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans, system-ui, sans-serif' }}
              />
            </div>
          )}

          {/* Bcc */}
          {showBcc && (
            <div className="flex items-center gap-2 px-5 py-1">
              <span className="text-sm font-mono shrink-0" style={{ color: 'var(--text-tertiary)', width: '32px' }}>Bcc</span>
              <input
                type="text"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none"
                style={{ color: 'var(--text-primary)', fontFamily: 'DM Sans, system-ui, sans-serif' }}
              />
            </div>
          )}

          {/* Subject */}
          <div
            className="flex items-center gap-2 px-5 py-2"
            style={{ borderTop: showCc || showBcc ? '1px solid var(--border)' : 'none' }}
          >
            <span className="text-sm font-mono shrink-0" style={{ color: 'var(--text-tertiary)', width: '32px' }}>Subj</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject line"
              className="flex-1 bg-transparent text-sm outline-none"
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'DM Sans, system-ui, sans-serif',
              }}
            />
          </div>
        </div>

        {/* Formatting toolbar */}
        <div
          className="flex items-center gap-1 px-5 py-2 shrink-0"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <button
            className="w-7 h-7 flex items-center justify-center rounded text-sm font-bold transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            title="Bold"
          >
            B
          </button>
          <button
            className="w-7 h-7 flex items-center justify-center rounded text-sm italic transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            title="Italic"
          >
            I
          </button>
          <button
            className="w-7 h-7 flex items-center justify-center rounded text-sm underline transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            title="Link"
            aria-label="Insert link"
          >
            <LinkIcon />
          </button>
          <button
            className="w-7 h-7 flex items-center justify-center rounded text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
            title="List"
            aria-label="Insert list"
          >
            <ListIcon />
          </button>
          <div className="flex-1" />
          <button
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors"
            style={{ color: 'var(--accent)', backgroundColor: 'var(--accent-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--text-inverse)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--accent-muted)'; e.currentTarget.style.color = 'var(--accent)'; }}
          >
            <AIWriteIcon /> Write with AI <span className="font-mono text-xs opacity-70">(Sprint 3.1)</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden px-5 py-3">
          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onFocus={() => setFocusedField('body')}
            placeholder="Write your message..."
            className="w-full h-full resize-none outline-none text-sm"
            style={{
              color: 'var(--text-primary)',
              fontFamily: 'DM Sans, system-ui, sans-serif',
              lineHeight: 1.7,
              backgroundColor: 'transparent',
            }}
          />
        </div>

        {/* Attachment zone hint */}
        <div
          className="mx-5 mb-2 px-4 py-3 rounded-lg text-center text-xs transition-colors cursor-pointer"
          style={{
            border: '1px dashed var(--border-strong)',
            color: 'var(--text-tertiary)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.color = 'var(--accent)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--border-strong)';
            e.currentTarget.style.color = 'var(--text-tertiary)';
          }}
        >
          Drop files here or click to attach
        </div>

        {/* Actions */}
        <div
          className="flex items-center gap-3 px-5 py-3 shrink-0"
          style={{ borderTop: '1px solid var(--border)' }}
        >
          {sendError && (
            <p className="text-xs" style={{ color: 'var(--error)' }} role="alert">
              {sendError}
            </p>
          )}
          <button
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--text-inverse)' }}
            onClick={() => void handleSend()}
            disabled={isSending || !to.trim() || !body.trim()}
          >
            {isSending ? 'Sending…' : 'Send'}
          </button>
          <button
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{
              color: 'var(--accent)',
              border: '1px solid var(--accent)',
              backgroundColor: 'transparent',
            }}
          >
            Schedule
          </button>
          <div className="flex-1" />
          <button
            className="px-3 py-2 rounded-lg text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onClick={() => {
              if (body || subject || to) setShowDiscardConfirm(true);
              else onClose();
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg-hover)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-tertiary)'; }}
          >
            Discard
          </button>
          <span className="text-xs font-mono" style={{ color: 'var(--text-tertiary)' }}>
            Cmd Enter
          </span>
        </div>
      </div>

      {/* Discard confirmation */}
      {showDiscardConfirm && (
        <>
          <div
            className="fixed inset-0 z-[60]"
            style={{ backgroundColor: 'var(--bg-overlay)' }}
            onClick={() => setShowDiscardConfirm(false)}
          />
          <div
            className="fixed left-1/2 top-1/2 z-[70] rounded-xl px-6 py-5 flex flex-col gap-4"
            style={{
              width: '360px',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'var(--bg-elevated)',
              border: '1px solid var(--border-strong)',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              Discard draft?
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Your message hasn't been sent. Are you sure you want to discard it?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                className="px-4 py-2 rounded-lg text-sm transition-colors"
                style={{ color: 'var(--text-secondary)' }}
                onClick={() => setShowDiscardConfirm(false)}
              >
                Keep editing
              </button>
              <button
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: 'var(--error)', color: 'var(--text-inverse)' }}
                onClick={onClose}
              >
                Discard
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
